import { GetIO } from '../types';
import { useSignatureV1Queries } from './use-signature-v1-queries';
import { UseSignatureV1QueryEndpointArgs, useSignatureV1Query } from './use-signature-v1-query';

/**
 * A hook that returns a query for the `Get` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetSignatureQuery = <E = unknown, D = GetIO['output']>(
  args: UseSignatureV1QueryEndpointArgs<'Get', E, D>
) =>
  useSignatureV1Query({
    endpointName: 'Get',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `Get` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetSignatureQueries = <E = unknown, D = GetIO['output']>(
  argsArr: UseSignatureV1QueryEndpointArgs<'Get', E, D>[]
) =>
  useSignatureV1Queries(
    argsArr.map((args) => ({
      endpointName: 'Get',
      ...args,
    }))
  );
